import type { ReactNode } from "react";
import {
  Box,
  Column,
  IconButton,
  Row,
  Text,
  AppleIcon,
  ChevronDownIcon,
} from "@hightouch/htui";
import { AndroidIcon, ChromeIcon, HightouchIcon } from "./push-icons";

export function IosPreview({
  title,
  body,
  bodyContent,
  showTitlePlaceholder = true,
}: PushCardProps) {
  return (
    <Column gap={2}>
      <Row gap={2} align="center">
        <Box as={AppleIcon} color="text.secondary" mb={0.5} boxSize={3.5} />
        <Text fontWeight="medium">iOS</Text>
      </Row>
      {/* Semi-transparent backdrop standing in for the lock-screen wallpaper —
          translucent so canvas/page content shows through and the dynamic
          content highlighting stays legible. */}
      <Box
        bg="blackAlpha.40"
        borderRadius="5xl"
        p={4}
        position="relative"
        overflow="hidden"
      >
        {/* Frosted glass notification card */}
        <Box
          bg="rgba(255, 255, 255, 0.18)"
          backdropFilter="blur(20px)"
          borderRadius="3xl"
          p={3}
          border="1px solid rgba(255, 255, 255, 0.12)"
        >
          <Row gap={2.5} align="flex-start">
            {/* App icon — large rounded square */}
            <Box
              w="36px"
              h="36px"
              bg="white"
              borderRadius="lg"
              flexShrink={0}
              display="flex"
              alignItems="center"
              justifyContent="center"
              overflow="hidden"
            >
              <HightouchIcon size={28} />
            </Box>
            <Column flex={1} gap={0.5} minW={0}>
              <Row justify="space-between" align="center" gap={4}>
                {(title || showTitlePlaceholder) && (
                  <Text fontWeight="semibold" color="white" isTruncated>
                    {title || "Message title"}
                  </Text>
                )}
                <Box
                  as={Text}
                  size="sm"
                  color="white"
                  flexShrink={0}
                  opacity="0.6"
                  ml="auto"
                >
                  37m ago
                </Box>
              </Row>
              {bodyContent ? (
                <Box as="span" color="white" fontSize="sm">
                  {bodyContent}
                </Box>
              ) : (
                <Text color="white" size="sm">
                  {body || "Message body"}
                </Text>
              )}
            </Column>
          </Row>
        </Box>
      </Box>
    </Column>
  );
}

export function AndroidPreview({
  title,
  body,
  bodyContent,
  showTitlePlaceholder = true,
}: PushCardProps) {
  return (
    <Column gap={2}>
      <Row gap={2} align="center">
        <AndroidIcon size={16} />
        <Text fontWeight="medium">Android</Text>
      </Row>
      <Box
        bg="blackAlpha.40"
        borderRadius="5xl"
        p={4}
        position="relative"
        overflow="hidden"
      >
        {/* White notification card */}
        <Box bg="base.lightBackground" borderRadius="sm" p={3}>
          <Row gap={2} align="flex-start">
            {/* App icon */}
            <Box
              w="18px"
              h="18px"
              bg="blue.500"
              borderRadius="sm"
              flexShrink={0}
              mt="1px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              overflow="hidden"
            >
              <AndroidIcon size={14} bodyColor="white" />
            </Box>
            <Column flex={1} minW={0}>
              <Row align="center" gap={2} justifyContent="space-between">
                <Row alignItems="center" gap={1} overflow="hidden">
                  {(title || showTitlePlaceholder) && (
                    <>
                      <Text
                        fontWeight="medium"
                        color="text.primary"
                        size="sm"
                        isTruncated
                      >
                        {title || "Message title"}
                      </Text>
                      <Text color="text.tertiary" size="sm">
                        ·
                      </Text>
                    </>
                  )}
                  <Text color="text.tertiary" size="sm">
                    5m
                  </Text>
                </Row>
                <IconButton
                  aria-label="expand"
                  variant="secondary"
                  icon={ChevronDownIcon}
                  isDisabled
                  shape="circle"
                  size="xs"
                  onClick={() => {}}
                />
              </Row>
              {bodyContent ? (
                <Box as="span" color="text.secondary" fontSize="sm">
                  {bodyContent}
                </Box>
              ) : (
                <Text color="text.secondary" size="sm">
                  {body || "Message body"}
                </Text>
              )}
            </Column>
          </Row>
        </Box>
      </Box>
    </Column>
  );
}

export function WebPreview({
  title,
  body,
  bodyContent,
  showTitlePlaceholder = true,
}: PushCardProps) {
  return (
    <Column gap={2}>
      <Row gap={2} align="center">
        <ChromeIcon size={18} />
        <Text fontWeight="medium">Web</Text>
      </Row>
      <Box
        bg="blackAlpha.40"
        borderRadius="5xl"
        p={4}
        position="relative"
        overflow="hidden"
      >
        {/* White notification card */}
        <Box bg="base.lightBackground" borderRadius="sm" p={3}>
          <Row gap={2} align="flex-start">
            {/* Chrome icon */}
            <Box flexShrink={0} mt="1px">
              <ChromeIcon size={18} />
            </Box>
            <Column flex={1} minW={0}>
              <Row align="center" gap={2} justifyContent="space-between">
                <Row alignItems="center" gap={1} overflow="hidden">
                  {(title || showTitlePlaceholder) && (
                    <>
                      <Text
                        fontWeight="medium"
                        color="text.primary"
                        size="sm"
                        isTruncated
                      >
                        {title || "Message title"}
                      </Text>
                      <Text color="text.tertiary" size="sm">
                        ·
                      </Text>
                    </>
                  )}
                  <Text color="text.tertiary" size="sm">
                    5m
                  </Text>
                </Row>
                <IconButton
                  aria-label="expand"
                  variant="secondary"
                  icon={ChevronDownIcon}
                  isDisabled
                  shape="circle"
                  size="xs"
                  onClick={() => {}}
                />
              </Row>
              {bodyContent ? (
                <Box as="span" color="text.secondary" fontSize="sm">
                  {bodyContent}
                </Box>
              ) : (
                <Text color="text.secondary" size="sm">
                  {body || "Message body"}
                </Text>
              )}
            </Column>
          </Row>
        </Box>
      </Box>
    </Column>
  );
}

type PushCardProps = {
  title?: string;
  body?: string;
  /** Rich body content (e.g. the canvas's block spans) — wins over `body`. */
  bodyContent?: ReactNode;
  /** Prompts for a missing title, which suits an authoring surface and not a message that already shipped. */
  showTitlePlaceholder?: boolean;
};

/** All three platforms at once. Card order is load-bearing for the dynamic
 * content canvas, which measures the FIRST rendering of each span — edges and
 * their badges land on the iOS card. */
export function PushPreview(cards: PushCardProps) {
  return (
    <Column gap={8} w="sm">
      <IosPreview {...cards} />
      <AndroidPreview {...cards} />
      <WebPreview {...cards} />
    </Column>
  );
}
